// 백엔드 호출. 오류 응답은 RFC 9457 ProblemDetail 이며 code 로 분기한다.

export const API_BASE_URL = (process.env.NEXT_PUBLIC_API_BASE_URL ?? "https://lapi.icgak.com").replace(/\/$/, "");
export const APP_ENV = process.env.NEXT_PUBLIC_APP_ENV ?? "loc";

const TOKEN_KEY = "icgak.accessToken";

export function getToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* 저장소 차단 환경: 세션 동안만 유지되지 않음 */
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string>;
  readonly props: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown> | null) {
    const detail = typeof body?.detail === "string" ? body.detail : "요청을 처리하지 못했습니다.";
    super(detail);
    this.status = status;
    this.code = typeof body?.code === "string" ? body.code : `HTTP_${status}`;
    this.fieldErrors = (body?.errors as Record<string, string>) ?? {};
    this.props = body ?? {};
  }
}

type Method = "GET" | "POST" | "PUT" | "DELETE";

/** 인증 만료(401) 시 호출할 콜백. AuthProvider 가 등록한다. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

export async function api<T>(method: Method, path: string, body?: unknown, init?: { auth?: boolean }): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = init?.auth === false ? null : getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, { detail: "서버에 연결할 수 없습니다. 네트워크 상태를 확인해 주세요.", code: "NETWORK" });
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }
  if (!res.ok) {
    if (res.status === 401 && token && onUnauthorized) onUnauthorized();
    throw new ApiError(res.status, (json as Record<string, unknown>) ?? null);
  }
  return json as T;
}

export const get = <T>(path: string) => api<T>("GET", path);
export const post = <T>(path: string, body?: unknown) => api<T>("POST", path, body ?? {});
export const put = <T>(path: string, body?: unknown) => api<T>("PUT", path, body ?? {});
export const del = <T>(path: string) => api<T>("DELETE", path);

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return "알 수 없는 오류가 발생했습니다.";
}
